import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  IonInput,
  IonPage,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonIcon,
  IonLabel,
  IonButton,
  IonImg,
  IonContent
} from '@ionic/react';
import { eyeOutline, eyeOffOutline } from 'ionicons/icons';
import '../styles/LoginForm.css';
import "../fonts.css";
import { handleLogin, handleLogout, handleRefreshToken, parseJwt, getAllSites } from "../service/adminUI";
import { useHistory, useLocation } from 'react-router';
import AppContext from './AppContext';
import { baseurl, clientID } from "../util/config";
import SessionManager from '../service/SessionManager';
import SessionInvalidationService from '../service/SessionInvalidationService';


// F4-17: Derive refresh interval from JWT expiry instead of hardcoded 4 min.
// Refreshes at 75% of token lifetime, with a floor of 30s and ceiling of 4 min.
function getRefreshIntervalFromToken(accessToken) {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    if (payload.exp && payload.iat) {
      const lifetimeMs = (payload.exp - payload.iat) * 1000;
      const interval = Math.floor(lifetimeMs * 0.75);
      return Math.max(30000, Math.min(interval, 240000));
    }
  } catch (e) {
    // Fall through to default
  }
  return 240000; // fallback: 4 minutes
}

export const LoginForm = () => {

  const [message, setMessage] = useState('');
  const [toastClass, setToastClass] = useState('toast-invisible');
  const [refreshToken, setRefreshToken] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [userId, setUserId] = useState('');
  const history = useHistory();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [totp, setTotp] = useState('');
  const [showSitePicker, setShowSitePicker] = useState(false);
  const [availableSites, setAvailableSites] = useState([]);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  // F4-16: Use ref for refresh token to avoid stale closure issues
  const refreshTokenRef = useRef('');

  const { appData, setAppData, refreshTimerId, setRefreshTimerId } = useContext(AppContext);

  const { setUserData } = useContext(AppContext);

  const OAUTH_URL = baseurl + 'auth/admin/calyan/console/'

  // Check for session invalidation reason on component mount
  useEffect(() => {
    const invalidationReason = SessionInvalidationService.getInvalidationReason();
    if (invalidationReason) {
      const css = 'toast' + ' ' + 'toast-error';
      setToastClass(css);
      setMessage(invalidationReason);
    }
  }, []); 

  //login••••
  const handleLoginOnClick = async () => {
    const result = await handleLogin(username, password, totp);
    if (result === 400) {
      const css = 'toast' + ' ' + 'toast-partial';
      setToastClass(css);
      //clarityxdevfd3@gmail.com
      setMessage(<body>Partial Account Setup..Please <a href="" onClick={() => openInNewTab()} > click here</a>, to complete the account setup</body>);
    } else if (result === 401) {
      const css = 'toast' + ' ' + 'toast-error';
      setToastClass(css);
      setMessage('Login unsuccessful, Please check your details');
    } else {
      const css = 'toast' + ' ' + 'toast-success';
      const parsedData = parseJwt(result.access_token);

      setMessage('Login Successful. Please wait');

      //set refresh token and access token for further fetch calls
      // This will generate a new session ID and invalidate any existing sessions
      SessionManager.setSession(result.access_token, result.refresh_token);
      
      // Broadcast new session to invalidate other tabs/windows
      const sessionId = SessionInvalidationService.getCurrentSessionId();
      if (sessionId) {
        SessionInvalidationService.broadcastNewSession(sessionId);
      }

      // Start the scheduled refresh token timer (every 4 minutes)
      startFetchingRefreshToken(result.refresh_token, result.access_token);

      setToastClass(css);
      setTimeout(async () => {

        setAccessToken(result.access_token);
        setUserId(parsedData.sub);
        setRefreshToken(result.refresh_token);
        const userSites = Array.isArray(parsedData.sites) ? parsedData.sites.map(String) : [];
        const role = parsedData.type;

        if (role === "clinician" && userSites.length > 1) {
          // Multiple sites — show site picker before navigating
          setUserData({ userId: parsedData.sub, userName: parsedData.name, userType: role, emailId: parsedData.email, sites: userSites, activeSiteId: "" });
          setPendingLoginData({ role, userSites });
          // Use the site IDs already present in the JWT — no extra API call needed
          setAvailableSites(userSites.map(id => ({ attributes: { siteId: id, name: 'Site ' + id } })));
          setShowSitePicker(true);
        } else if (role === "clinician") {
          const activeSite = userSites.length === 1 ? userSites[0] : "";
          setUserData({ userId: parsedData.sub, userName: parsedData.name, userType: role, emailId: parsedData.email, sites: userSites, activeSiteId: activeSite });
          history.push("/clinician");
        } else if (role === "admin") {
          setUserData({ userId: parsedData.sub, userName: parsedData.name, userType: role, emailId: parsedData.email, sites: userSites, activeSiteId: "" });
          history.push({ pathname: '/dashboard/home' });
        } else {
          // Unrecognized role — clear session and show error
          if (refreshTimerId?.timerId) {
            clearInterval(refreshTimerId.timerId);
            setRefreshTimerId({ timerId: null });
          }
          setAppData({ count: 0, refreshToken: "", accessToken: "" });
          SessionManager.clearSession();
          const css = 'toast' + ' ' + 'toast-error';
          setToastClass(css);
          setMessage('User not authorized to access the application');
        }

      }, 2000);
    }
    //handleLogout('');z
  };

  //redirect user to login page on session expiry
  const logoutOnTokenExpiry = async () => {
    try {
      // Clear interval first to prevent further refresh attempts
      if (refreshTimerId?.timerId) {
        clearInterval(refreshTimerId.timerId);
      }
      setRefreshTimerId({ timerId: null });
      
      // Clear session data
      setAppData({ count: 0, refreshToken: "", accessToken: "" });
      SessionManager.clearSession();
      
      // Show message
      const css = 'toast' + ' ' + 'toast-error';
      setToastClass(css);
      setMessage('Session expired. Please login again');
      
      // Redirect to login
      history.push('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Force redirect even if there's an error
      SessionManager.clearSession();
      history.push('/login');
    }
  }

  //start timer on login success, before this
  //refresh token must be set from first login response
  function startFetchingRefreshToken(refreshtoken, accesstoken) {
    // F4-16: Store refresh token in ref to avoid stale closure
    refreshTokenRef.current = refreshtoken;
    setAppData({ count: 1, refreshToken: refreshtoken, accessToken: accesstoken });
    var temp = 1;

    // F4-17: Derive interval from actual JWT expiry
    const fetchTokenFreq = getRefreshIntervalFromToken(accesstoken);

    //start timer for background fetch refresh token
    var timerHandle = setInterval(async () => {
      try {
        temp++;
        // F4-16: Read current refresh token from ref (not stale closure variable)
        const currentRefreshToken = refreshTokenRef.current;
        const result = await handleRefreshToken(currentRefreshToken);

        // Check if result is an error code or valid response
        if (result === 400 || result === 401) {
          // Clear interval immediately to prevent further attempts
          clearInterval(timerHandle);
          setRefreshTimerId({ timerId: null });

          // 401 indicates session was invalidated (concurrent login detected)
          if (result === 401) {
            const sessionId = SessionInvalidationService.getCurrentSessionId();
            if (sessionId) {
              SessionInvalidationService.broadcastSessionInvalidation(
                sessionId,
                'Your session was invalidated because you logged in on another device or browser.'
              );
            }
          } else {
            // 400 indicates refresh token expired
            console.warn('Refresh token expired. Session will be terminated.');
          }
          await logoutOnTokenExpiry();
          return; // Exit early to prevent further execution
        }

        // Check if result is valid (has access_token and refresh_token)
        if (result && result.access_token && result.refresh_token) {
          // F4-16: Update ref with new refresh token
          refreshTokenRef.current = result.refresh_token;
          SessionManager.setSession(result.access_token, result.refresh_token);
          setAppData({ count: temp, refreshToken: result.refresh_token, accessToken: result.access_token });
        } else {
          // Invalid response - clear interval and logout
          clearInterval(timerHandle);
          setRefreshTimerId({ timerId: null });
          await logoutOnTokenExpiry();
        }
      } catch (error) {
        // Catch any unexpected errors in the refresh process
        console.error('Error during token refresh:', error);
        // Don't clear interval on unexpected errors - might be network issues
        // Only clear if it's a clear authentication failure
        if (error?.response?.status === 400 || error?.response?.status === 401) {
          clearInterval(timerHandle);
          setRefreshTimerId({ timerId: null });
          await logoutOnTokenExpiry();
        }
      }
    }, fetchTokenFreq);

    // update Context
    setRefreshTimerId({ timerId: timerHandle });
  }

  //get anchor tag with URL to open OAuth authorization flow
  //The OAuth flow will automatically detect and handle required actions after login
  const openInNewTab = () => {
    // Use OAuth authorization endpoint - this handles login AND required actions
    const redirectUri = window.location.origin + "/login";
    const authUrl = baseurl + "auth/realms/calyan/protocol/openid-connect/auth";
    
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientID,
      redirect_uri: redirectUri,
      scope: "openid",
    });

    const fullUrl = `${authUrl}?${params.toString()}`;
    const newWindow = window.open(fullUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const handleUsernameOnChange = (event) => {
    //setUsername(event.currentTarget.value);
    setUsername(event.detail.value);
  };
  const handlePasswordOnChange = (event) => {
    //setPassword(event.currentTarget.value);
    setPassword(event.detail.value);
  };
  const handleEyeIconOnClick = () => {
    setPasswordVisible(!passwordVisible);
  };
  const handleOtpOnChange = (event) => {
    //setUsername(event.currentTarget.value);
    setTotp(event.detail.value);
  };

  const handleSiteSelection = (siteId) => {
    setUserData(prev => ({ ...prev, activeSiteId: siteId }));
    setShowSitePicker(false);
    if (pendingLoginData?.role === "clinician") {
      history.push("/clinician");
    }
  };

  if (showSitePicker) {
    return (
      <div className="login-form-container">
        <div className="login-form" style={{ textAlign: 'center' }}>
          <h3 style={{ marginBottom: '20px', fontFamily: 'var(--app-font-sans)', fontWeight: 600 }}>Select Your Site</h3>
          <p style={{ marginBottom: '20px', fontSize: '14px', color: 'var(--app-ink-3)' }}>
            You belong to multiple sites. Please select which site to work with:
          </p>
          {availableSites.map((site) => (
            <button
              key={site.attributes?.siteId}
              className="continue-button"
              style={{ marginBottom: '10px', display: 'block', width: '100%' }}
              onClick={() => handleSiteSelection(site.attributes?.siteId)}
            >
              {site.attributes?.name || 'Site ' + site.attributes?.siteId}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="login-form-container">
      <form onSubmit={(e) => { e.preventDefault(); handleLoginOnClick(); }} className="login-form">

        <div className="input-group">
          <input
            type="text"
            className="form-input"
            placeholder="USERNAME"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            data-testid="TGETLOGINUSER"
          />
        </div>

        <div className="input-group">
          <input
            type={passwordVisible ? 'text' : 'password'}
            className="form-input"
            placeholder="PASSWORD"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="TGETLOGINID"
          />
          <IonIcon
            className='password-toggle'
            icon={passwordVisible ? eyeOutline : eyeOffOutline}
            onClick={handleEyeIconOnClick}
          />
        </div>

        <div className="forgot-password">
          <button
            type="button"
            className="forgot-link"
            onClick={() => {
              history.push('/forgotPassword');
            }}
          >
            forgot password
          </button>
        </div>

        <div className="input-group">
          <input
            type="text"
            className="form-input"
            placeholder="OTP"
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            data-testid="TGETLOGINOTP"
          />
        </div>

        <button
          type="submit"
          className="continue-button"
          data-testid="TGETLOGINDATA"
        >
          CONTINUE
        </button>

        <div className="terms-text">
          By signing in, you accept our{" "}
          <button
            type="button"
            className="terms-link"
            onClick={() => {
              // TODO: navigate to Terms of Service page
            }}
          >
            Terms of Service
          </button>{" "}
          and{" "}
          <button
            type="button"
            className="terms-link"
            onClick={() => {
              // TODO: navigate to Privacy Policy page
            }}
          >
            Privacy Policy
          </button>
        </div>

        <div data-testid="TGETLOGINMSG" className={toastClass}>{message}</div>
      </form>
    </div>
  );
};
export default LoginForm;
